interface GetTextWidthFunction {
  (text: string, fontWeight: string, fontSize: number, fontFamily: string): {width: number; height: number};
  canvas?: HTMLCanvasElement;
}

/**
 * Calculates the dimensions of a text string as it would appear on a DOM canvas.
 *
 * This is useful for estimating the dimensions of a text string that might appear as a label
 * on a canvas element.
 *
 * @param text - The text string to calculate the dimensions of.
 * @param fontWeight - The weight of the font to use.
 * @param fontSize - The size of the font to use.
 * @param fontFamily - The family of the font to use.
 * @returns The width and height of the text string.
 */
export const getTextDimensions: GetTextWidthFunction = function (
  text: string,
  fontWeight: string,
  fontSize: number,
  fontFamily: string
) {
  const canvas = getTextDimensions.canvas || (getTextDimensions.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');

  // getContext can technically return null. For our use case, it's really only if
  // canvas is not supported - but here's a fallback to satisfy typescript
  if (!context) {
    return {
      // if we can't draw the text to get actual width, we're just approximating
      // a per-character width as an (unlikely) fallback
      width: text.length * 4,
      height: 12,
    };
  }

  context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return {
    width: metrics.width,
    height: metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent,
  };
};
